import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { logger } from '@/lib/sentry';
import type { models_DunningSettings, organisations_DunningSettingsResponse } from '@/api';

export const dunningKeys = {
  all: ['dunning'] as const,
  settings: (orgId?: string | null) => [...dunningKeys.all, 'settings', orgId ?? null] as const,
  templates: () => [...dunningKeys.all, 'templates'] as const,
  template: (stage: string, language: string) =>
    [...dunningKeys.all, 'templates', stage, language] as const,
};

export function useDunningSettings() {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: dunningKeys.settings(orgId),
    // The response carries `sweeper_active` next to the stored settings: whether
    // a reminder can leave at all, whatever `enabled` says.
    queryFn: async (): Promise<organisations_DunningSettingsResponse> => {
      if (!orgId) throw new Error('No organisation ID available');
      const { OrganisationSettingsService } = await import('@/api');
      return OrganisationSettingsService.getApiV1OrganisationsDunningSettings(orgId);
    },
    enabled: isSignedIn && isAuthReady && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateDunningSettings() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const { orgId } = useAuth();

  return useMutation({
    mutationFn: async (settings: models_DunningSettings) => {
      if (!orgId) throw new Error('No organisation ID available');
      const { OrganisationSettingsService } = await import('@/api');
      const response =
        await OrganisationSettingsService.putApiV1OrganisationsDunningSettings(orgId, settings);
      return response as models_DunningSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(dunningKeys.settings(orgId), data);
      queryClient.invalidateQueries({ queryKey: dunningKeys.settings(orgId) });
      // Seeding may have just fired; refresh templates list as well.
      queryClient.invalidateQueries({ queryKey: dunningKeys.templates() });
      toast.success(i18n._(msg({ id: 'dunningSettings.toast.saved', message: 'Dunning settings saved' })));
    },
    onError: (error: unknown) => {
      logger.error('Dunning settings update failed', {
        context: 'dunningSettings.update',
        error,
      });
      toast.error(i18n._(msg({ id: 'dunningSettings.toast.saveFailed', message: 'Failed to save dunning settings' })));
    },
  });
}
